import { useTranslation } from 'react-i18next';
import tw, { TwStyle } from 'twin.macro';

import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import {
  CategoryType,
  EpisodeGarmFilter,
  GarmRiskLevelType,
} from '@insights_module/types/BrandSafety';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';

const HeatMapCategory = tw.div`w-full flex items-center justify-start p-2`;
const HeatMapCategoryName = tw.span`text-[11px] w-[181px] min-w-[181px] truncate text-text-and-icon`;
const HeatMapCategoryRail = tw.div`w-full bg-sec-1 h-2 relative rounded-[2px]`;
const HeatMapOccurrence = tw.span`absolute h-[calc(100% - 1px)] top-[0.5px] rounded-[2px] border-[0.7px] border-solid box-border cursor-pointer`;

export type HeatmapDataType = {
  id: number;
  start: number;
  end: number;
  category: string;
  risk: GarmRiskLevelType;
}[][];

export type HeatMapPropsType = {
  duration: number;
  onTimeUpdate: (val: number) => void;
  heatmapData: HeatmapDataType;
  episodeGarmResults: EpisodeGarmFilter[];
};

type HeatMapOccurrenceType = {
  id: number;
  start: number;
  category: CategoryType;
};

const OCCURRENCE_STYLES = {
  unrelated: tw`border-garm-unrelated-dark bg-garm-unrelated-bg` as TwStyle,
  low: tw`border-garm-low-dark bg-garm-low-bg` as TwStyle,
  medium: tw`border-garm-medium-dark bg-garm-medium-bg` as TwStyle,
  high: tw`border-garm-high-dark bg-garm-high-bg` as TwStyle,
  floor: tw`border-garm-floor-dark bg-floor-bg` as TwStyle,
};

const SELECTED_OCCURRENCE_STYLES = {
  unrelated: tw`bg-garm-unrelated-bg` as TwStyle,
  low: tw`bg-garm-low-bg` as TwStyle,
  medium: tw`bg-garm-medium-bg` as TwStyle,
  high: tw`bg-garm-high-bg` as TwStyle,
  floor: tw`bg-floor-bg` as TwStyle,
};

export const GarmHeatMap = ({
  episodeGarmResults = [],
  duration,
  onTimeUpdate,
  heatmapData,
}: HeatMapPropsType) => {
  const { t } = useTranslation();
  const { selectedSegment } = useEpisodeSegment();
  const {
    setSelectedSegment,
    setSelectedGarmCategory,
    setSelectedGarmLevel,
    selectedGarmCategory,
  } = useEpisodeDetailsContext();

  function handleClick({ id, start, category }: HeatMapOccurrenceType) {
    onTimeUpdate(start);
    setSelectedSegment(id);
    setSelectedGarmCategory(category);
    setSelectedGarmLevel(null);
  }

  return (
    <div data-testid='garm-heat-map'>
      {episodeGarmResults.map((garm) => {
        if (garm.risk === GARM_RISK_LEVEL.unrelated) {
          return null;
        }

        return (
          <HeatMapCategory key={garm.category} data-testid='garm-heat-category'>
            <HeatMapCategoryName>
              {t(`insights.garm_category.${garm.category}`)}
            </HeatMapCategoryName>
            <HeatMapCategoryRail>
              {heatmapData?.map((segment) =>
                segment.map((occurrence) => {
                  if (occurrence.category === garm.category) {
                    const isSelected =
                      selectedSegment?.id === occurrence.id &&
                      occurrence.category === selectedGarmCategory;

                    return (
                      <HeatMapOccurrence
                        data-testid={`${occurrence.id}-${occurrence.category}`}
                        key={`${occurrence.id}-${occurrence.category}`}
                        onClick={() => {
                          handleClick({
                            id: occurrence.id,
                            start: occurrence.start,
                            category: garm.category,
                          });
                        }}
                        css={[
                          {
                            left: `${(occurrence.start / duration) * 100}%`,
                            right: `${100 - (occurrence.end / duration) * 100}%`,
                          },
                          OCCURRENCE_STYLES[occurrence.risk.toLowerCase() as GarmRiskLevelType],
                          isSelected &&
                            SELECTED_OCCURRENCE_STYLES[
                              occurrence.risk.toLowerCase() as GarmRiskLevelType
                            ],
                        ]}
                      />
                    );
                  }
                })
              )}
            </HeatMapCategoryRail>
          </HeatMapCategory>
        );
      })}
    </div>
  );
};
